import { CombinationsOptions, GlobalOptions } from '@/enums/global/GlobalOptions';
import { CanonicalSegmentName } from '@/helpers/SegmentNameMapping';
import { CanvasTemplateConfiguration } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';

// =====================================================================
// Types
// =====================================================================

export type CombinationsConfiguratorType =
    | 'standard'
    | 'shutters'
    | 'shutters-alternative'
    | 'puro'
    | 'hero'
    | 'hero-2'
    | 'hero-1';

export interface CombinationsChecklistFieldConfig {
    pid: GlobalOptions | string;
    label: string;
    type: string;
    active: boolean;
    shouldUpdateConfigurator: boolean;
    isReadOnly: boolean;
    segment: string;
    noChoiceValue?: string;
    options?: Array<{ label: string; value: string }>;
    step?: number;
    value?: boolean | string | number;
}

export type CombinationsChecklistFields = Record<string, CombinationsChecklistFieldConfig>;

export interface CombinationsConfigurationItem {
    products: string[];
    segment: CanonicalSegmentName;
    configuratorType: CombinationsConfiguratorType;
    configuratorSteps: number;
    shouldAddCombinationInMultiposition: boolean;
    showCombinationType: boolean;
    maximumNumberOfCombinations: number;
    maximumAssemblySize: number;
    normalizeCombinationsSizeType: 'selectedSize' | 'depthAndHeight';
    checklistFields: CombinationsChecklistFields;
    canvasTemplateConfiguration: CanvasTemplateConfiguration;
}

// =====================================================================
// Canvas template configurations (guide assets, scaling, offsets)
// =====================================================================

export const standardCanvasTemplate: CanvasTemplateConfiguration = {
    guides: {
        left: require('@/assets/guides/left-guide.svg'),
        right: require('@/assets/guides/right-guide.svg'),
        center: require('@/assets/guides/center-guide.svg'),
    },
    scale: 1.5,
    guideLeftOffset: 30,
    rightOffset: 30,
    rightOffsetX: 80,
    middleGuideOffsetX: 40,
    guideYOffset: -45,
    descriptionYOffset: 110,
    windowGroupYOffset: 200,
    extraMarginForCanvasStage: 400,
};

const heroSharedSizes = {
    scale: 1.2,
    guideLeftOffset: 5,
    rightOffset: -5,
    rightOffsetX: 40,
    middleGuideOffsetX: 50,
    guideYOffset: -20,
    guideRightOffsetY: -20,
    descriptionYOffset: 220,
    windowGroupYOffset: 320,
    extraMarginForCanvasStage: 520,
};

export const heroOneCanvasTemplate: CanvasTemplateConfiguration = {
    ...heroSharedSizes,
    alternativeMiddleGuideOffsetX: 60,
    guides: {
        left: require('@/assets/guides/left-guide-hero.png'),
        right: require('@/assets/guides/right-guide-hero.png'),
        center: require('@/assets/guides/center-guide-hero.png'),
        alternativeCenter: require('@/modules/ConfiguratorCombinations/assets/hero-1-alternative-guide.png'),
    },
};

export const heroTwoCanvasTemplate: CanvasTemplateConfiguration = {
    ...heroSharedSizes,
    guides: {
        left: require('@/assets/guides/left-guide-hero.png'),
        right: require('@/assets/guides/right-guide-hero.png'),
        center: require('@/assets/guides/center-guide-hero.png'),
        alternativeCenter: require('@/modules/ConfiguratorCombinations/assets/hero-2-alternative-guide.png'),
    },
};

// =====================================================================
// Configurator step descriptions
// =====================================================================

export const combinationsConfiguratorSteps = [
    {
        name: 'Selection',
        description:
            'As the first step in configuring the units, select the positions that will make up the cabinet. With the configurator, you can choose 2 or 3 positions for the unit. If you need more positions, please contact us. If positions are already included in a unit, they will be marked.',
    },
    {
        name: 'Sequence',
        description: 'Choose the order of positions (from left to right).',
    },
    {
        name: 'Size',
        description:
            'Select the total width of the combined cabinet. The total width must be equal to or at most equal to the sum of the widths of both positions.',
    },
    {
        name: 'Configuration',
        description:
            'Select the drive side and the guides between the assemblies. Be sure that the option to select a partition guide is only available when the heights of the positions are the same. If the heights are different, you only have the option to select single guides. Based on your selections, the code for the combined cabinet will be generated automatically.',
    },
];

// =====================================================================
// Checklist field definitions per configurator variant
// =====================================================================

const baseFields: CombinationsChecklistFields = {
    PRODUCT_ID: {
        pid: 'productId',
        label: 'Num.',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    SISTEM_PID: {
        pid: CombinationsOptions.SISTEM_PID,
        label: 'System',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    QUANTITY_PID: {
        pid: CombinationsOptions.QUANTITY_PID,
        label: 'Quantity',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    WIDTH_PID: {
        pid: CombinationsOptions.WIDTH_PID,
        label: 'Width',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    HEIGHT_PID: {
        pid: CombinationsOptions.HEIGHT_PID,
        label: 'Height',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    POSITION_PID: {
        pid: CombinationsOptions.POSITION_PID,
        label: 'Position',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    ASSEMBLY_POSITION_PID: {
        pid: CombinationsOptions.ASSEMBLY_POSITION_PID,
        label: 'Assembly Position',
        type: 'dropdown',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'table',
        options: [
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
        ],
        step: 2,
    },
    DRIVE_PID: {
        pid: CombinationsOptions.DRIVE_PID,
        label: 'Drive',
        type: 'dropdown',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'configurator',
    },
    TOTAL_WIDTH_PID: {
        pid: CombinationsOptions.TOTAL_WIDTH_PID,
        label: 'Total Width',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'totalWidth',
    },
    COMBINATION_TYPE_PID: {
        pid: CombinationsOptions.COMBINATION_TYPE_PID,
        label: 'Combination type',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
    },
    NUMBER_OF_COMBINATIONS_PID: {
        pid: CombinationsOptions.NUMBER_OF_COMBINATIONS_PID,
        label: 'Number of combinations',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    COMBINATIONS_PID: {
        pid: CombinationsOptions.COMBINATIONS_PID,
        label: 'Combinations',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        value: true,
    },
    LEFT_GUIDE_PID: {
        pid: GlobalOptions.LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    RIGHT_GUIDE_PID: {
        pid: GlobalOptions.RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.ROLLER_SHUTTER_BOX_SIZE_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    DIMENSIONS_TYPE_PID: {
        pid: CombinationsOptions.DIMENSIONS_TYPE_PID,
        label: 'Dimensions Type',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: '',
    },
    FINAL_WIDTH_PID: {
        pid: CombinationsOptions.FINAL_WIDTH_PID,
        label: 'Final Width',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: '',
    },
};

export const shuttersFields: CombinationsChecklistFields = {
    ...baseFields,
};

const shuttersAlternativeFields: CombinationsChecklistFields = {
    ...baseFields,
    LEFT_GUIDE_PID: {
        pid: GlobalOptions.ALTERNATIVE_LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    RIGHT_GUIDE_PID: {
        pid: GlobalOptions.ALTERNATIVE_RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
};

const puroFields: CombinationsChecklistFields = {
    ...baseFields,
    LEFT_GUIDE_PID: {
        pid: GlobalOptions.ALTERNATIVE_LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    RIGHT_GUIDE_PID: {
        pid: GlobalOptions.ALTERNATIVE_RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: 'Brez',
    },
    DRIVE_PID: {
        pid: CombinationsOptions.DRIVE_PID,
        label: 'Drive',
        type: 'dropdown',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: false,
        segment: 'configurator',
    },
    COMBINATION_TYPE_PID: {
        pid: CombinationsOptions.COMBINATION_TYPE_PID,
        label: 'Combination type',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'configurator',
    },
    ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.ROLLER_SHUTTER_BOX_DEPTH_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.ROLLER_SHUTTER_BOX_HEIGHT_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
};

const heroTwoFields: CombinationsChecklistFields = {
    ...baseFields,
    LEFT_GUIDE_PID: {
        pid: GlobalOptions.LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: '',
    },
    RIGHT_GUIDE_PID: {
        pid: GlobalOptions.RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: 'configurator',
        noChoiceValue: '',
    },
    COMBINATIONS_PID: {
        pid: GlobalOptions.HERO_COMBINATIONS_PID,
        label: 'Hero Combinations',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        value: true,
    },
    COMBINED_ASSEMBLY_PID: {
        pid: GlobalOptions.COMBINED_ASSEMBLY_PID,
        label: 'Combined Assembly',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        value: true,
    },
    TOTAL_WIDTH_PID: {
        pid: GlobalOptions.HERO_TOTAL_WIDTH_PID,
        label: 'Total Width',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'totalWidth',
    },
    HERO_GUIDE_CARRIER_PID: {
        pid: GlobalOptions.HERO_GUIDE_CARRIER_PID,
        label: 'Hero Guide Carrier',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: '',
    },
};

const heroFields: CombinationsChecklistFields = {
    ...heroTwoFields,
    ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.ROLLER_SHUTTER_BOX_DEPTH_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.ROLLER_SHUTTER_BOX_HEIGHT_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
};

const heroOneFields: CombinationsChecklistFields = {
    ...heroTwoFields,
    FLOOR_MOUNTING_HERO_PID: {
        pid: GlobalOptions.FLOOR_MOUNTING_HERO_PID,
        label: 'Floor Mounting Hero',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    RIGHT_GUIDE_HOLDER_PID: {
        pid: GlobalOptions.RIGHT_GUIDE_HOLDER_PID,
        label: 'Right Guide Holder',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    LEFT_GUIDE_HOLDER_PID: {
        pid: GlobalOptions.LEFT_GUIDE_HOLDER_PID,
        label: 'Left Guide Holder',
        type: 'boolean',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.CONSOLE_SIZE_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID: {
        pid: CombinationsOptions.SELECTED_CONSOLE_SIZE_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    HERO_MASK_TYPE_PID: {
        pid: CombinationsOptions.HERO_MASK_TYPE_PID,
        label: 'Hero Mask Type',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
    HERO_GUIDE_LINING_PID: {
        pid: CombinationsOptions.HERO_GUIDE_LINING_PID,
        label: 'Hero Guide Lining',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
    },
};

// =====================================================================
// Per-product configuration
// =====================================================================

export const combinationsConfiguration: CombinationsConfigurationItem[] = [
    {
        products: ['PENTO', 'INTEGO', 'RONDO', 'Q.INTEGO', 'QUADRO.E'],
        segment: CanonicalSegmentName.SESTAVLJENA_OMARICA,
        configuratorType: 'standard',
        configuratorSteps: 4,
        shouldAddCombinationInMultiposition: true,
        showCombinationType: true,
        maximumNumberOfCombinations: 3,
        maximumAssemblySize: 1500,
        normalizeCombinationsSizeType: 'selectedSize',
        checklistFields: shuttersFields,
        canvasTemplateConfiguration: standardCanvasTemplate,
    },
    {
        products: ['RA.2', 'RA.3', 'KARO'],
        segment: CanonicalSegmentName.OMARICA,
        configuratorType: 'shutters',
        configuratorSteps: 4,
        shouldAddCombinationInMultiposition: false,
        showCombinationType: false,
        maximumNumberOfCombinations: 3,
        maximumAssemblySize: 1500,
        normalizeCombinationsSizeType: 'selectedSize',
        checklistFields: shuttersFields,
        canvasTemplateConfiguration: standardCanvasTemplate,
    },
    {
        products: ['VISIO'],
        segment: CanonicalSegmentName.OMARICA,
        configuratorType: 'shutters-alternative',
        configuratorSteps: 4,
        shouldAddCombinationInMultiposition: false,
        showCombinationType: false,
        maximumNumberOfCombinations: 3,
        maximumAssemblySize: 1500,
        normalizeCombinationsSizeType: 'selectedSize',
        checklistFields: shuttersAlternativeFields,
        canvasTemplateConfiguration: standardCanvasTemplate,
    },
    {
        products: ['PURO.2.XR'],
        segment: CanonicalSegmentName.LAMELE,
        configuratorType: 'puro',
        configuratorSteps: 3,
        shouldAddCombinationInMultiposition: false,
        showCombinationType: false,
        maximumNumberOfCombinations: 3,
        maximumAssemblySize: 1500,
        normalizeCombinationsSizeType: 'depthAndHeight',
        checklistFields: puroFields,
        canvasTemplateConfiguration: standardCanvasTemplate,
    },
    {
        products: ['HERO.5', 'HERO.5X'],
        segment: CanonicalSegmentName.VODILA,
        configuratorType: 'hero',
        configuratorSteps: 4,
        shouldAddCombinationInMultiposition: false,
        showCombinationType: false,
        maximumNumberOfCombinations: 2,
        maximumAssemblySize: 300,
        normalizeCombinationsSizeType: 'depthAndHeight',
        checklistFields: heroFields,
        canvasTemplateConfiguration: standardCanvasTemplate,
    },
    {
        products: ['HERO.2'],
        segment: CanonicalSegmentName.VODILA,
        configuratorType: 'hero-2',
        configuratorSteps: 4,
        shouldAddCombinationInMultiposition: false,
        showCombinationType: false,
        maximumNumberOfCombinations: 2,
        maximumAssemblySize: 300,
        normalizeCombinationsSizeType: 'selectedSize',
        checklistFields: heroTwoFields,
        canvasTemplateConfiguration: heroTwoCanvasTemplate,
    },
    {
        products: ['HERO.1'],
        segment: CanonicalSegmentName.VODILA,
        configuratorType: 'hero-1',
        configuratorSteps: 4,
        shouldAddCombinationInMultiposition: false,
        showCombinationType: false,
        maximumNumberOfCombinations: 2,
        maximumAssemblySize: 300,
        normalizeCombinationsSizeType: 'selectedSize',
        checklistFields: heroOneFields,
        canvasTemplateConfiguration: heroOneCanvasTemplate,
    },
];
